import * as React from 'react';
import { cn } from '../../lib/utils';
import { useIsTruncated } from '../../lib/useIsTruncated';
import { TruncationTooltip } from './TruncationTooltip';

/** Clamp classes are spelled out (not `line-clamp-${n}`) so Tailwind's JIT
 *  keeps them. Extend only with literals if more line counts are needed. */
const CLAMP_CLASS = {
  1: 'truncate',
  2: 'line-clamp-2',
  3: 'line-clamp-3',
} as const;

/** Focus ring, applied only when the element is focusable (i.e. actually
 *  clipped — see `tabIndex`). Mirrors IconTooltipButton's tokens so keyboard
 *  users who tab to a clipped cell get a visible focus target. */
const FOCUS_RING =
  'rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1';

interface TruncatedTextProps {
  /** Text rendered in the cell, measured for overflow, and (by default) shown
   *  in the tooltip. Pass the display string — use `content` when the reveal
   *  should differ (e.g. an abbreviated header) and `children` when the display
   *  is custom markup. */
  text: string;
  /** Element to render. Defaults to `span`. */
  as?: 'span' | 'p' | 'div';
  /** Typography/layout classes only — the clamp class (`truncate` /
   *  `line-clamp-N`) is added automatically from `lines`, so omit it here. */
  className?: string;
  /** 1 = single-line `truncate` (default); 2/3 = `line-clamp-N` (vertical). */
  lines?: 1 | 2 | 3;
  /** Reveal content, defaulting to `text`. Use when the visible text is
   *  abbreviated or should read differently on hover (e.g. `@handle`). */
  content?: React.ReactNode;
  /** Rendered when `text` is empty — a placeholder (e.g. `—`) that is neither
   *  measured nor revealed. */
  placeholder?: React.ReactNode;
  /** Custom display markup (e.g. an icon + text). Still measured and revealed
   *  via `text`; falls back to rendering `text` when omitted. */
  children?: React.ReactNode;
  side?: React.ComponentProps<typeof TruncationTooltip>['side'];
  /** Merged over the wrapping defaults (max-width + multi-line). */
  contentClassName?: string;
}

/**
 * Truncated text that reveals its full value on hover only when actually
 * clipped by its container. The single shared convenience layer over
 * `useIsTruncated` + `TruncationTooltip` — reach for the primitive directly
 * only for genuinely custom compositions (a bespoke truncation gate, a
 * composite/multi-field reveal, or an interactive `<button>` trigger).
 */
export function TruncatedText({
  text,
  as = 'span',
  className,
  lines = 1,
  content,
  placeholder,
  children,
  side,
  contentClassName,
}: TruncatedTextProps) {
  const [ref, isTruncated] = useIsTruncated<HTMLElement>(text);
  const Tag = as as React.ElementType;
  const body = children ?? (text.length > 0 ? text : placeholder);

  return (
    <TruncationTooltip
      isTruncated={isTruncated}
      content={content ?? text}
      side={side}
      contentClassName={contentClassName}
    >
      {/* Focusable only when actually clipped, so keyboard users can reveal the
          hidden text (Radix opens the tooltip on focus) without adding a tab
          stop to every fully-visible cell. */}
      <Tag
        ref={ref}
        tabIndex={isTruncated ? 0 : undefined}
        className={cn(CLAMP_CLASS[lines], isTruncated && FOCUS_RING, className)}
      >
        {body}
      </Tag>
    </TruncationTooltip>
  );
}
